/**
 * Release identity for browser RUM (spec 68 §5.3).
 *
 * A minified stack trace is only half the problem. Dash0 resolves frames
 * by fetching the deployed `.js` and following its `//# sourceMappingURL=`
 * comment to the `.js.map` — so translation depends on the *currently
 * deployed* assets, and a frame from an older build silently fails with
 * "Position not found in source map".
 *
 * That makes the release stamp load-bearing rather than decorative: it is
 * how you tell "unresolvable because that build is gone" apart from
 * "unresolvable because our source maps are broken". Without it both look
 * identical in the UI.
 *
 * ## Why this takes arguments instead of reading process.env
 *
 * It used to read `process.env` itself. That silently produced nothing in
 * the browser, and the build proved it: the emitted chunk still contained
 * the literal string `NEXT_PUBLIC_APP_VERSION` rather than the value.
 *
 * Two reasons, either of which is fatal on its own:
 *
 * 1. Bundlers replace only a *literal* `process.env.FOO` member expression.
 *    Reaching it through a captured `env` object — `const e = process.env;
 *    e.FOO` — is a dynamic lookup no bundler can statically resolve.
 * 2. This package ships pre-built (tsdown → dist). Next compiles app code,
 *    not our dist, so a `process.env` reference in here would never be
 *    visited by the inliner even if it were written literally.
 *
 * So the literals must appear in the *app's* own source. This function
 * takes the values; `instrumentation-client.ts` supplies them as literal
 * `process.env.NEXT_PUBLIC_*` reads. The rules about what counts as
 * present live here, where they are testable.
 */

/** The subset of @dash0/sdk-web's InitOptions this module owns. */
export interface RumReleaseOptions {
  serviceVersion?: string;
  environment?: string;
  deploymentId?: string;
}

export interface RumReleaseInput {
  /** Literal `process.env.NEXT_PUBLIC_APP_VERSION` from the app's source. */
  version?: string;
  /** Literal `process.env.NEXT_PUBLIC_ENV_TIER` from the app's source. */
  tier?: string;
}

/**
 * Build the release-identity slice of a RUM `init()` call.
 *
 * Every field is omitted rather than emitted empty when its input is
 * absent. That is deliberate: `serviceVersion: ''` is a *claim* that the
 * build has no version, and it would group every unversioned event under
 * one meaningless bucket. An absent key leaves the attribute unset, which
 * is the honest representation of "we do not know".
 */
export function rumReleaseOptions(input: RumReleaseInput = {}): RumReleaseOptions {
  const out: RumReleaseOptions = {};

  // Set by next.config.mjs from NEXT_DEPLOYMENT_ID, which the deploy
  // workflow passes as the release tag. Doubles as both the human-facing
  // version and the id that pins a stack trace to one set of deployed
  // chunks.
  const version = input.version?.trim();
  if (version) {
    out.serviceVersion = version;
    out.deploymentId = version;
  }

  // prod / dev. Keeps a local build's RUM errors from being read as prod
  // incidents, which matters more once errors are something you triage.
  const tier = input.tier?.trim();
  if (tier === 'prod' || tier === 'dev') out.environment = tier;

  return out;
}
